import 'shaka-player/dist/controls.css';
import shaka from 'shaka-player/dist/shaka-player.ui.js';
import Simplestream from '../simplestream.js';
import Debug from '../debug.js';
import Heartbeat from '../heartbeat.js';
import Nielsen from '../nielsen.js';
import PauseAd from '../pauseAd.js';
import PlayNext from '../playNext.js';
import './plugin.css';
import Bowser from "bowser";
import player from '../simplestream.js';

export default class SimplestreamPlayer {
    constructor(id, plugins) {
        this.id = id;
        this.plugins = plugins;
        this.scripts = [];
        this.pauseAdsEnabled = true;
        this.playNextEnabled = false;
        this.nielsenEnabled = true;
        this.debugEnabled = false;
        this.containers();
    }

    /**
     * Render player containers.
     */
    containers() {
        console.log('SimplestreamPlayer::containers()');

        // Get container
        this.container = document.getElementById(this.id);
        this.container.classList.add('simplestream');

        // Create 16x9 container
        this.aspectRatioContainer = document.createElement('div');
        this.aspectRatioContainer.classList.add('aspect-ratio-16x9');
        this.container.appendChild(this.aspectRatioContainer);
        this.aspectRatioContainerContent = document.createElement('div');
        this.aspectRatioContainerContent.classList.add('aspect-ratio-16x9-content');
        this.aspectRatioContainer.appendChild(this.aspectRatioContainerContent);

        // Create shaka player container
        this.shakaContainer = document.createElement('div');
        this.shakaContainer.setAttribute('id', 'shaka-container-' + this.id);
        this.shakaContainer.classList.add('shaka-video-container');
        this.aspectRatioContainerContent.appendChild(this.shakaContainer);

        // Create <video> element inside shaka player container
        this.videoElement = document.createElement('video');
        this.videoElement.setAttribute('id', 'shaka-' + this.id); 
        this.videoElement.setAttribute('autoplay', '');
        this.videoElement.setAttribute('muted', '');
        this.videoElement.setAttribute('playsinline', '');
        this.videoElement.setAttribute('poster', this.container.dataset.poster);
        this.shakaContainer.classList.add('shaka-video');
        this.shakaContainer.appendChild(this.videoElement);

        // Create ad banner
        this.adBanner = document.createElement('div');
        this.adBanner.classList.add('simplestream-ad-banner');
        this.adBanner.setAttribute('style', 'position: absolute; background: red; color: white; width: 100%; padding: 5px; top: 0; left: 0; z-index: 1000; display: none;');
        this.shakaContainer.appendChild(this.adBanner);

        // Create ad UI
        this.adUi = document.createElement('div');
        this.adBanner.classList.add('simplestream-ad-ui');
        this.adUi.setAttribute('style', 'position: absolute; width: 100%; height: 100%; top: 0; left: 0;');
        this.shakaContainer.appendChild(this.adUi);

        return this.loadScripts();
    }

    /**
     * Load 3rd-party scripts.
     */
    loadScripts() {
        console.log('SimplestreamPlayer::scripts()', this.id, this.plugins);

        if(this.plugins.includes('youbora')) {
            console.log('Include Youbora Plugin v7.2.35');
            this.scripts.push('https://artifact.plugin.npaw.com/artifactory/plugins/js/7.2.35/NpawPlugin-nwf.js');
            this.scripts.push('https://artifact.plugin.npaw.com/artifactory/plugins/js/7.2.35/NpawPluginAdapters.js');
        }

        if(this.plugins.includes('ima')) {
            console.log('Include Google IMA & DAI Plugins');
            this.scripts.push('https://imasdk.googleapis.com/js/sdkloader/ima3.js');
            this.scripts.push('https://imasdk.googleapis.com/js/sdkloader/ima3_dai.js');
        }

        // Filter out URLs of scripts already included in the document
        this.scripts = this.scripts.filter(url => {
            const isScriptIncluded = document.querySelector(`script[src="${url}"]`);
            if (isScriptIncluded) {
                console.log('Script already included:', url);
                return false; // Exclude this URL from the new array
            }
            return true; // Include this URL in the new array
        });

        console.log('Scripts to load:', this.scripts);

        // Create an array of promises for each script
        const promises = this.scripts.map(url => new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.onload = () => resolve(url); // Resolve the promise on success
            script.onerror = () => reject(url); // Reject the promise on error
            document.head.appendChild(script);
        }));

        // Use Promise.all to wait for all promises to resolve or reject
        Promise.all(promises)
            .then(results => {
                console.log('All scripts loaded successfully:', results);
                this.init();
            })
            .catch(error => {
                console.error('Failed to load some scripts:', error);
                this.errorScreen('Please disable your ad blocker and refresh the page.');
            });
    }

    /**
     * Init Simplestream plugin.
     */
    async init() {
        new Simplestream(this.id);
        document.addEventListener('simplestream-ready', (data) => {
            this.ready(data.detail);
        }, { once: true });
    }

    /**
     * Init player when ready.
     */
    async ready(data) {
        if(!this.isSupportedBrowser()){
            return this.errorScreen('Your mobile browser is not supported. Please use Safari, Chrome, Firefox, or Microsoft Edge.');
        }
        console.log('SimplestreamPlayer::ready()', data);
        if(data.playerApiResponse.company_id == '196') { // VM
            if(this.isMobileBrowser()) {
                return this.mobileScreen(data.playerApiResponse.company_id);
            }
        }

        // Init NPAW as early as possible
        if(typeof NpawPlugin !== "undefined") {
            this.npaw = new NpawPlugin('simplestream', {'components': {'balancer': false}});
            this.npaw.setLogLevel(LogLevel.DEBUG);
        }

        // Init Shaka Player
        const videoElement = document.getElementById('shaka-' + this.id);
        const videoContainerElement = document.getElementById('shaka-container-' + this.id);

        this.SimplestreamPlayer = new shaka.Player();
        this.SimplestreamPlayerUi = new shaka.ui.Overlay(this.SimplestreamPlayer, videoContainerElement, videoElement);
        await this.SimplestreamPlayer.attach(videoElement);

        const controls = this.SimplestreamPlayerUi.getControls();
        const player = controls.getPlayer();
        const video = controls.getVideo();

        this.SimplestreamPlayerUi.configure({
            'addBigPlayButton' : false,
            'enableTooltips' : true,
            'customContextMenu' : false,
            'contextMenuElements' : ['statistics', 'ad_statistics'],
        });

        // Set the networking engine
        if (shaka.net.HttpXHRPlugin) {
            shaka.net.NetworkingEngine.registerScheme('http', shaka.net.HttpXHRPlugin.parse, shaka.net.NetworkingEngine.PluginPriority.PREFERRED);
            shaka.net.NetworkingEngine.registerScheme('https', shaka.net.HttpXHRPlugin.parse, shaka.net.NetworkingEngine.PluginPriority.PREFERRED);
        }

        let nielsenAnalytics = data.analyticsApiResponse.nielsen_analytics;

        if(this.npaw) {
            this.npaw.registerAdapterFromClass(player, NpawPluginAdapters.video.Shaka, null, this.id, () => {
                let videoOptions = data.analyticsApiResponse;
                delete videoOptions['accountCode'];
                delete videoOptions['nielsen_analytics'];
                console.log('videoOptions', videoOptions);
                this.npaw.setVideoOptions(videoOptions, this.id);
            });
        }

        let uri = null;

        // Configure drm
        if (data.streamsApiResponse.drm?.widevine) {
            uri = data.streamsApiResponse.drm.widevine.stream;
            player.configure({
                "drm": {
                    "servers": {
                        "com.widevine.alpha": data.streamsApiResponse.drm.widevine.licenseAcquisitionUrl,
                    },
                },
            });
        } else if (data.streamsApiResponse.drm?.fairplay) {
            uri = data.streamsApiResponse.drm.fairplay.stream;
            shaka.polyfill.PatchedMediaKeysApple.install();
            const FairPlayUtils = shaka.util.FairPlayUtils;
            player.getNetworkingEngine().registerRequestFilter(FairPlayUtils.expressplayFairPlayRequest);
            player.getNetworkingEngine().registerResponseFilter(FairPlayUtils.commonFairPlayResponse);
            player.configure('drm.initDataTransform', FairPlayUtils.expressplayInitDataTransform);
            player.configure('drm.advanced.com\\.apple\\.fps.serverCertificateUri', data.streamsApiResponse.drm.fairplay.certificate);
            player.configure({
                "drm": {
                    "servers": {
                        "com.apple.fps": data.streamsApiResponse.drm.fairplay.licenseAcquisitionUrl,
                    },
                },
            });
        } else if (data.streamsApiResponse.drm?.playready) {
            player.configure({
                "drm": {
                    "servers": {
                        'com.microsoft.playready': data.streamsApiResponse.drm.playready.licenseAcquisitionUrl + '?ExpressPlayToken=' + data.streamsApiResponse.drm.playready.token,
                    },
                "advanced": {
                    licenseRequestHeaders: {
                        'Authorization': `Bearer ${data.streamsApiResponse.drm.playready.token}`
                    }
                }
                }
            });
        } else {
            uri = data.streamsApiResponse.stream;
        }

        // Listen for error events.
        player.addEventListener('error', this.onErrorEvent);
        controls.addEventListener('error', this.onErrorEvent);

        // Use playing event to determine if the stream is playing
        const eventManager = new shaka.util.EventManager();
        this.currentTime = 0;
        this.setDataAttribute('stream', 'ready');
        
        const updateStreamStatus = () => {
            if (this.currentTime != videoElement.currentTime && !videoElement.paused) {
                //console.log('Stream is playing.');
                this.setDataAttribute('stream', 'playing');
            } else {
                //console.log('Stream is paused.');
                this.setDataAttribute('stream', 'paused');
            }
            this.currentTime = videoElement.currentTime;
        };

        eventManager.listen(videoElement, 'playing', updateStreamStatus);
        eventManager.listen(videoElement, 'timeupdate', updateStreamStatus);

        const adManager = player.getAdManager();

        const adManagerEvents = [
            shaka.ads.Utils.AD_BREAK_READY,
            shaka.ads.Utils.AD_BUFFERING,
            shaka.ads.Utils.AD_CLICKED,
            shaka.ads.Utils.AD_CLOSED,
            shaka.ads.Utils.AD_COMPLETE,
            shaka.ads.Utils.AD_CONTENT_ATTACH_REQUESTED,
            shaka.ads.Utils.AD_CONTENT_PAUSE_REQUESTED,
            shaka.ads.Utils.AD_CONTENT_RESUME_REQUESTED,
            shaka.ads.Utils.AD_DURATION_CHANGED,
            shaka.ads.Utils.AD_ERROR,
            shaka.ads.Utils.AD_FIRST_QUARTILE,
            shaka.ads.Utils.AD_IMPRESSION,
            shaka.ads.Utils.AD_INTERACTION,
            shaka.ads.Utils.AD_LINEAR_CHANGED,
            shaka.ads.Utils.AD_LOADED,
            shaka.ads.Utils.AD_METADATA,
            shaka.ads.Utils.AD_MIDPOINT,
            shaka.ads.Utils.AD_MUTED,
            shaka.ads.Utils.AD_PAUSED,
            shaka.ads.Utils.AD_PROGRESS,
            shaka.ads.Utils.AD_RECOVERABLE_ERROR,
            shaka.ads.Utils.AD_RESUMED,
            shaka.ads.Utils.AD_SKIP_STATE_CHANGED,
            shaka.ads.Utils.AD_SKIPPED,
            shaka.ads.Utils.AD_STARTED,
            shaka.ads.Utils.AD_STOPPED,
            shaka.ads.Utils.AD_THIRD_QUARTILE,
            shaka.ads.Utils.AD_VOLUME_CHANGED,
            shaka.ads.Utils.ADS_LOADED,
            shaka.ads.Utils.ALL_ADS_COMPLETED,
            shaka.ads.Utils.CUEPOINTS_CHANGED,
            shaka.ads.Utils.IMA_AD_MANAGER_LOADED,
            shaka.ads.Utils.IMA_STREAM_MANAGER_LOADED,
        ];

        adManagerEvents.forEach(eventType => {
            adManager.addEventListener(eventType, (event) => {
                console.log('shaka.ads.AdManager Event: ' + eventType);
                this.setDataAttribute('preroll', eventType);
            });
        });

        // Google Preroll
        let disableCsai = new URLSearchParams(new URL(window.location.href).search).get('disableCsai');
        if(typeof google !== "undefined" 
            && !disableCsai
            && data.streamsApiResponse.ads?.csai?.preroll?.vast) {

            this.setDataAttribute('preroll', 'ready');

            const container = controls.getClientSideAdContainer();
            adManager.initClientSide(container, videoElement);

            const adsRequest = new google.ima.AdsRequest();
            adsRequest.adTagUrl = data.streamsApiResponse.ads.csai.preroll.vast;
            adManager.requestClientSideAds(adsRequest);

        } else {
            this.setDataAttribute('preroll', 'all-ads-completed');
        }

        // Google Video Stitcher SSAI
        if(data.streamsApiResponse.ads?.ssai?.provider == 'google-video-stitcher') {

            adManager.addEventListener(shaka.ads.AdManager.IMA_STREAM_MANAGER_LOADED, (e) => {
                this.imaStreamManager = e['imaStreamManager'];
            });

            /*adManager.addEventListener(shaka.ads.AdManager.IMA_STREAM_MANAGER_LOADED, (e) => {
                console.log('shaka.ads.AdManager Event: ' + shaka.ads.AdManager.IMA_STREAM_MANAGER_LOADED);
                const imaStreamManager = e['imaStreamManager'];
                imaStreamManager.addEventListener([
                    google.ima.dai.api.StreamEvent.Type.LOADED,
                    google.ima.dai.api.StreamEvent.Type.AD_BREAK_STARTED,
                    google.ima.dai.api.StreamEvent.Type.AD_BREAK_ENDED,
                    google.ima.dai.api.StreamEvent.Type.AD_PERIOD_STARTED,
                    google.ima.dai.api.StreamEvent.Type.AD_PERIOD_ENDED,
                    google.ima.dai.api.StreamEvent.Type.AD_PROGRESS,
                    google.ima.dai.api.StreamEvent.Type.CUEPOINTS_CHANGED,
                    google.ima.dai.api.StreamEvent.Type.CLICK,
                    google.ima.dai.api.StreamEvent.Type.ERROR,
                    google.ima.dai.api.StreamEvent.Type.STARTED,
                    google.ima.dai.api.StreamEvent.Type.FIRST_QUARTILE,
                    google.ima.dai.api.StreamEvent.Type.MIDPOINT,
                    google.ima.dai.api.StreamEvent.Type.STREAM_INITIALIZED,
                    google.ima.dai.api.StreamEvent.Type.THIRD_QUARTILE,
                    google.ima.dai.api.StreamEvent.Type.COMPLETE,
                    google.ima.dai.api.StreamEvent.Type.SKIPPABLE_STATE_CHANGED,
                    google.ima.dai.api.StreamEvent.Type.SKIPPED,
                    google.ima.dai.api.StreamEvent.Type.VIDEO_CLICKED,
                    google.ima.dai.api.StreamEvent.Type.PAUSED,
                    google.ima.dai.api.StreamEvent.Type.RESUMED,
                ], (e) => {
                    try {

                        // Debug info
                        switch (e.type) {
                            case google.ima.dai.api.StreamEvent.Type.LOADED:
                                console.log('DAI Event: ' + e.type);
                                console.warn('Google Video Stitcher streamId: ', e.getStreamData().streamId);
                                break;
                            default:
                                console.log('DAI Event: ' + e.type, e);

                                const ad = e.getAd();
                                if(ad) {
                                    const adPod = ad.getAdPodInfo();
                                    console.log("  - Ad Title: " + ad.getTitle());
                                    console.log("  - Ad Duration: " + Math.floor(ad.getDuration()));
                                    console.log("  - Ad Pod Duration: ", Math.floor(adPod.getMaxDuration()));
                                }

                                const streamData = e.getStreamData();
                                const adProgressData = streamData.adProgressData;
                                if(adProgressData) {
                                    const currentTime = Math.floor(adProgressData.currentTime); // Current playback time of the ad
                                    const duration = Math.floor(adProgressData.duration); // Total duration of the ad
                                    const adPosition = Math.floor(adProgressData.adPosition); // Position of the ad in the ad pod
                                    const totalAds = Math.floor(adProgressData.totalAds); // Total number of ads in the ad pod
                                    console.log(`  - Ad Progress: ${currentTime}/${duration} seconds`);
                                    console.log(`  - Ad Position: ${adPosition} of ${totalAds}`);
                                    this.adBanner.innerText = `Ad ${adPosition} of ${totalAds} ${currentTime}s / ${duration}s`;

                                }

                                break;
                        }

                        // Hide/Show ad banner
                        switch (e.type) {
                            case google.ima.dai.api.StreamEvent.Type.AD_BREAK_STARTED:
                            case google.ima.dai.api.StreamEvent.Type.AD_PERIOD_STARTED:
                            case google.ima.dai.api.StreamEvent.Type.STARTED:
                            case google.ima.dai.api.StreamEvent.Type.FIRST_QUARTILE:
                            case google.ima.dai.api.StreamEvent.Type.MIDPOINT:
                            case google.ima.dai.api.StreamEvent.Type.THIRD_QUARTILE:
                            case google.ima.dai.api.StreamEvent.Type.COMPLETE:
                                this.adBanner.style.display = 'block';
                                break;
                            case google.ima.dai.api.StreamEvent.Type.AD_BREAK_ENDED:
                            case google.ima.dai.api.StreamEvent.Type.AD_PERIOD_ENDED:
                                this.adBanner.style.display = 'none';
                                break;
                            default:
                                break;
                        }

                    } catch (error) {
                        console.error("An error occurred:", error);
                    }
                }, false);
            });*/

            //const container = controls.getServerSideAdContainer();
            //console.log('getServerSideAdContainer()', container);
            adManager.initServerSide(this.adUi, videoElement);

            var streamRequest = new google.ima.dai.api.VideoStitcherLiveStreamRequest();
            streamRequest.projectNumber = data.streamsApiResponse.ads.ssai.projectNumber;
            streamRequest.customAssetKey = data.streamsApiResponse.ads.ssai.customAssetKey;
            streamRequest.format = 'hls';
            streamRequest.networkCode = data.streamsApiResponse.ads.ssai.networkCode;
            streamRequest.liveStreamEventId = data.streamsApiResponse.ads.ssai.liveStreamEventId;
            streamRequest.region = data.streamsApiResponse.ads.ssai.region;
            streamRequest.oAuthToken = data.streamsApiResponse.ads.ssai.oAuthToken.access_token;
            streamRequest.adTagParameters = data.streamsApiResponse.ads.ssai.midrollAdTagParameters;

            try {
                uri = await adManager.requestServerSideStream(streamRequest);
            } catch (error) {
                console.log(error);
                alert('Google Video Stitcher Error. Play underlying stream: ' + uri);
            }

        }

        // Media Tailor SSAI
        if(data.streamsApiResponse.ads?.ssai?.provider == 'media-tailor') {
            const container = controls.getServerSideAdContainer();
            const player = controls.getPlayer();
            const netEngine = player.getNetworkingEngine();

            adManager.initMediaTailor(container, netEngine, video);
            const mediaTailorUrl = data.streamsApiResponse.ads.ssai.prefix + data.streamsApiResponse.ads.ssai.session;
            const mediaTailorAdsParams = data.streamsApiResponse.ads.ssai.postBody;
            uri = await adManager.requestMediaTailorStream(mediaTailorUrl, mediaTailorAdsParams);

            // BUGFIX: Change beacons from POST to GET
            player.getNetworkingEngine().registerRequestFilter(function(type, request, context) {
                if (type != shaka.net.NetworkingEngine.RequestType.ADS) {
                    return;
                }
                if(request.method == 'POST') {
                    var image = new Image();
                    image.onload = function(e) {
                        //console.log('Loaded tracking pixel: ' + request.uris[0]);
                    };
                    image.onerror = function(e) {
                        console.error('Could not load tracking pixel.' + request.uris[0]);
                    };
                    image.referrerPolicy = "no-referrer";
                    image.src = request.uris[0];
                    request.abort();
                }
            });
        }

        // Load stream
        try {
            await player.load(uri);
            if(data.attributes.position) {
                console.log('Set Player Position To ' + data.attributes.position);
                player.getMediaElement().currentTime = data.attributes.position;
            }
            let loading = this.container.querySelector('.loading');
            if(loading) {
                loading.remove();
            }
        } catch (error) {
            this.onError(error);
        }

        this.loadPlugins(video, data, nielsenAnalytics);
        this.events(video);
    }
    isSupportedBrowser() {
        const browser = Bowser.getParser(window.navigator.userAgent);
        console.log('Checking browser', Bowser.parse(window.navigator.userAgent))

        if(!browser.is('mobile')){
            return true;
        }

        const isSupported = browser.satisfies({
            chrome: ">=89",
            firefox: ">=78",
            edge: ">=89",
            safari: ">=9",
        });

        console.warn("Is browser supported? " + (isSupported ? 'YES' : 'NO'))

        return isSupported;
    }
    loadPlugins(video, data, nielsenAnalytics) {
        console.log('SimplestreamPlayer::loadPlugins()');

        // Heartbeat Plugin
        if(data.streamsApiResponse?.heartbeat?.url) {
            new Heartbeat({
                'videoElement': video, // Video element to listen for pause event
                'companyId': data.playerApiResponse.company_id, // Company ID returned by Player API
                'contentId': data.attributes.uvid, // data-uvid attribute from the embed code
                'contentType': data.attributes.type, // data-type attribute from the embed code
                'url': data.streamsApiResponse.heartbeat.url, // Heartbeat URL returned by Streams API
                'userId': data.attributes.userId,
            });
        }

        // Nielsen Plugin
        if(this.nielsenEnabled) {
            new Nielsen({
                'id': this.id, // Unique ID for the player
                'videoElement': video, // Video element to listen for events
                'SimplestreamPlayer': player, // Shaka Player instance
                'shakaPlayerUi': this.SimplestreamPlayerUi, // Shaka UI
                'analyticsApiResponse': nielsenAnalytics, // Analytics API response
                'streamsApiResponse': data.streamsApiResponse,
                'playerTech': 'shaka',
            });
        }

        // PauseAd Plugin
        if(this.pauseAdsEnabled 
            && data.attributes.type != 'live' 
            && data.streamsApiResponse.ads?.pause?.vast) {
            new PauseAd({
                'container': this.aspectRatioContainerContent, // Container in which to render the pause ad
                'videoElement': video, // Video element to listen for pause event
                'vastTag': data.streamsApiResponse.ads.pause.vast, // Google tag returned by Streams API
            });
        }

        // PlayNext Plugin
        if(this.playNextEnabled
            && data.attributes.type != 'live') {
            new PlayNext({
                'parent': this, // Parent class
                'container': this.aspectRatioContainerContent, // Container in which to render the pause ad
                'videoElement': video, // Video element to listen for pause event
                'playerApiResponse': data.playerApiResponse, // Response from the Player API
                'attributes': data.attributes, // Data attributes from the embed code
            });
        }

        // Debug Plugin
        if(this.debugEnabled) {
            new Debug({
                'parent': this, // Parent class
                'container': this.aspectRatioContainerContent, // Container in which to render the debug overlay
                'videoElement': video, // Video element to listen for pause event
            });
        }
    }

    events(video) {

        console.log('SimplestreamPlayer::events()');

        // Define a list of events to track
        var videoEvents = [
            'loadstart', 'progress', 'suspend', 'abort', 'error', 'emptied', 'stalled',
            'loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough', 'playing', 'waiting',
            'seeking', 'seeked', 'ended', 'durationchange', 'timeupdate', 'play', 'pause',
            'ratechange', 'resize', 'volumechange'
        ];

        // Add event listeners for each event
        videoEvents.forEach(function(event) {
            video.addEventListener(event, () => {
                //console.log('Filter: SimplestreamPlayer Video Event', event);
            });
        });

    }

    captureGoogleVideoStitcherEvents(adManager) {

        /* Shaka Player unifies client-side and server-side ad events under the shaka.ads namespace.
            * https://shaka-player-demo.appspot.com/docs/api/lib_ads_ad_utils.js.html
            */
        const shakaAdEvents = [
            shaka.ads.Utils.ADS_LOADED,
            shaka.ads.Utils.AD_STARTED,
            shaka.ads.Utils.AD_FIRST_QUARTILE,
            shaka.ads.Utils.AD_MIDPOINT,
            shaka.ads.Utils.AD_THIRD_QUARTILE,
            shaka.ads.Utils.AD_COMPLETE,
            shaka.ads.Utils.AD_STOPPED,
            shaka.ads.Utils.AD_SKIPPED,
            shaka.ads.Utils.AD_VOLUME_CHANGED,
            shaka.ads.Utils.AD_MUTED,
            shaka.ads.Utils.AD_PAUSED,
            shaka.ads.Utils.AD_RESUMED,
            shaka.ads.Utils.AD_SKIP_STATE_CHANGED,
            shaka.ads.Utils.CUEPOINTS_CHANGED,
            shaka.ads.Utils.IMA_AD_MANAGER_LOADED,
            shaka.ads.Utils.IMA_STREAM_MANAGER_LOADED,
            shaka.ads.Utils.AD_CLICKED,
            shaka.ads.Utils.AD_PROGRESS,
            shaka.ads.Utils.AD_BUFFERING,
            shaka.ads.Utils.AD_IMPRESSION,
            shaka.ads.Utils.AD_DURATION_CHANGED,
            shaka.ads.Utils.AD_CLOSED,
            shaka.ads.Utils.AD_LOADED,
            shaka.ads.Utils.ALL_ADS_COMPLETED,
            shaka.ads.Utils.AD_LINEAR_CHANGED,
            shaka.ads.Utils.AD_METADATA,
            shaka.ads.Utils.AD_RECOVERABLE_ERROR,
            shaka.ads.Utils.AD_ERROR,
            shaka.ads.Utils.AD_BREAK_READY,
            shaka.ads.Utils.AD_INTERACTION,
            shaka.ads.Utils.AD_CONTENT_PAUSE_REQUESTED,
            shaka.ads.Utils.AD_CONTENT_RESUME_REQUESTED,
            shaka.ads.Utils.AD_CONTENT_ATTACH_REQUESTED
        ];

        shakaAdEvents.forEach(eventType => {
            adManager.addEventListener(eventType, (event) => {
                console.log('shaka.ads.Utils Event:' + event.type);
            });
        });

    }

    /**
     * Destroys the Shaka Player UI and Shaka Player instances.
     * 
     * @returns {Promise<void>} A promise that resolves when the destruction of both Shaka Player UI and Shaka Player is complete.
     * @throws {Error} An error message.
     */
    destroy() {
        const promises = [];

        if(this.npaw) {
            this.npaw.destroy();
        }

        if(this.imaStreamManager) {
            this.imaStreamManager.reset();
        }
    
        if (this.SimplestreamPlayerUi) {
            const uiDestroyPromise = this.SimplestreamPlayerUi.destroy()
                .then(() => {
                    console.log('Shaka Player UI destroyed successfully');
                })
                .catch((error) => {
                    console.error('Error destroying Shaka Player UI', error);
                    throw error;  // Re-throw to ensure the promise rejects
                });
            promises.push(uiDestroyPromise);
        }
    
        if (this.SimplestreamPlayer) {
            const playerDestroyPromise = this.SimplestreamPlayer.destroy()
                .then(() => {
                    console.log('Shaka Player destroyed successfully');
                    this.container.replaceChildren();
                })
                .catch((error) => {
                    console.error('Error destroying Shaka Player', error);
                    throw error;  // Re-throw to ensure the promise rejects
                });
            promises.push(playerDestroyPromise);
        }
    
        // Return a promise that resolves when all destruction promises resolve
        return Promise.all(promises);
    }

    /**
     * Set a data attribute on the video element.
     * @param {string} name - The name of the data attribute.
     * @param {string} value - The value of the data attribute.
     */
    setDataAttribute(name, value) {
        const video = document.getElementById('shaka-' + this.id);
        if (video) {
            video.setAttribute('data-' + name, value);
        }
    }

    onErrorEvent(event) {
        onError(event.detail);
    }
      
    onError(error) {
        console.error('Error code', error.code, 'object', error);
    }
    
    initFailed(errorEvent) {
        // Handle the failure to load; errorEvent.detail.reasonCode has a shaka.ui.FailReasonCode describing why.
        console.error('Unable to load the UI library!');
    }

    /**
     * Render fatal error screen.
     * @param {string} msg - The error message to display.
     */
    errorScreen(msg) {
        const error = document.createElement('div');
        error.setAttribute('class', 'error');
        this.container.querySelector('.aspect-ratio-16x9-content').innerHTML = '';
        this.container.querySelector('.aspect-ratio-16x9-content').appendChild(error);

        const img = document.createElement('img');
        img.src = 'data:image/png;base64,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';
        error.appendChild(img);

        const p = document.createElement('p');
        p.textContent = msg;
        error.appendChild(p);
    }
    
    isMobileBrowser() {
        const mediaQuery = window.matchMedia('(max-width: 768px)').matches || 
            window.matchMedia('(hover: none) and (pointer: coarse)').matches;
            
        const deviceCheck = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        return mediaQuery && deviceCheck;
    }

    mobileScreen(company_id) {
        console.log('Shaka::mobileScreen()');

        let appleUrl = '';
        let androidUrl = '';
        if(company_id == '196') { // Virgin Media
            appleUrl = 'https://apps.apple.com/gb/app/virgin-media-play/id602597705'
            androidUrl = 'https://play.google.com/store/apps/details?id=com.axonista.threeplayer&hl=en_GB&pli=1';
        }

        const error = document.createElement('div');
        error.setAttribute('class', 'mobile');
        error.style.display = 'flex';
        error.style.flexDirection = 'column';
        error.style.alignItems = 'center';
        error.style.textAlign = 'center';
        error.style.justifyContent = 'center';
        
        this.container.querySelector('.aspect-ratio-16x9-content').innerHTML = '';
        this.container.querySelector('.aspect-ratio-16x9-content').appendChild(error);
        
        const p = document.createElement('p');
        p.style.color = "white";
        p.textContent = 'For the best viewing experience, please use a desktop browser or download our free app.';
        error.appendChild(p);
        
        const apple = document.createElement('img');
        apple.src = '/static/images/apple-store-badge.png';
        apple.onclick = function () {
            window.location.href = appleUrl;
        };
        apple.style.margin = '10px 0';
        error.appendChild(apple);
        
        const android = document.createElement('img');
        android.src = '/static/images/google-play-badge.png';
        android.onclick = function () {
            window.location.href = androidUrl;
        };
        android.style.margin = '10px 0';
        error.appendChild(android);
    }

    get() {
        return this;
    }

    /**
     * Reload the player with a different UVID.
     * @param {string} uvid - The new UVID to load.
     */
    changeUvid(uvid) {
        // Get the container element by ID and update the data-uvid attribute
        this.container = document.getElementById(this.id);
        if (this.container) {
            this.container.dataset.uvid = uvid;
        } else {
            console.error('Container element not found.');
            return;
        }

        // Destroy and re-initialize the player
        this.destroy()
            .then(() => {
                this.containers();
            })
            .catch((error) => {
                console.error('Destruction failed:', error);
            });
    }
}